import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider,
  Modal,
  IconButton,
} from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import { contactFormSchema } from "./validationSchema";
import "./ContactUsHero.scss";
import { nameInputFilterRegex } from "../../../constants/regexStrings";
import NewPhoneNumberComponent from "../../../common/components/new-phone-input-component/new-phone-input-component";
import Recaptcha, {
  RecaptchaRef,
} from "../../../common/components/google-recaptcha-component/recaptcha";
import { Close } from "@mui/icons-material";
import { submitEnquiry } from "../../../api/contact-us";
import { FormData } from "../contact-us-page.types";
import { inquiryOptions, reasonForInquiryOptions } from "./options";

const initialValues: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  mobileNumber: "",
  inquiryMadeOnBehalfOf: "",
  reasonForInquiry: "",
  message: "",
  captchaToken: "",
};

const compactInputProps = {
  sx: {
    height: "42px",
    "& .MuiInputBase-root": {
      height: "42px",
    },
    "& input": {
      padding: "10px 14px !important",
      height: "42px",
      boxSizing: "border-box",
    },
    "& .MuiSelect-select": {
      padding: "10px 14px",
      height: "42px",
      display: "flex",
      alignItems: "center",
    },
    "& fieldset": {
      height: "100%",
    },
  },
};

export default function ContactUsHero() {
  // STATES
  const [countryCode, setCountryCode] = useState<string>("+1");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const captchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

  const recaptchaRef = useRef<RecaptchaRef>(null);

  // Email validation regex for submit button enablement
  const emailValidationRegex =
    /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // HANDLERS
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleFormSubmit = async (
    values: FormData,
    { resetForm, setFieldValue }: FormikHelpers<FormData>
  ) => {
    setIsLoading(true);
    try {
      // Format phone by prepending country code & cleaning non-digits
      const formattedPhone = `${countryCode}${values.mobileNumber.replace(/\D/g, "")}`;

      // Map to API-expected field names
      const submissionData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobileNumber: formattedPhone,
        enquiryMadeOnBehalfOf: values.inquiryMadeOnBehalfOf,
        reasonForEnquiry: values.reasonForInquiry,
        message: values.message,
        captchaToken: values.captchaToken,
      };

      // Submit with the existing valid token
      const response = await submitEnquiry(submissionData);

      if (response && (response.status === 201 || response.status === 200)) {
        // after success, reset everything
        resetForm();

        // clear captcha field after backend validates successfully
        setFieldValue("captchaToken", "");

        // reset the visual captcha
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        handleOpenModal();
      } else {
        alert("Enquiry submission failed.");
      }
    } catch (error: unknown) {
      console.error("Error: ", error);
      alert("Failed to send Enquiry");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecaptchaVerify = (
    captchaToken: string | null,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (captchaToken) {
      setFieldValue("captchaToken", captchaToken);

    } else {
      // if expired or reset by user
      setFieldValue("captchaToken", "");
    }
  };

  return (
    <Box className="contact-us-hero">
      <Box className="contact-us-hero__overlay">
        <Typography variant="h5" className="contact-us-hero__title">
          Contact Us
        </Typography>
        <Typography variant="h5" className="contact-us-hero__description">
          Any question or remarks? Just write us a message!
        </Typography>

        <Box className="contact-us-hero__form-wrapper">
          <Typography variant="h5" className="contact-us-hero__form-title">
            Get In Touch
          </Typography>
          <Divider className="contact-us-hero__divider" />

          <Formik<FormData>
            initialValues={initialValues}
            validationSchema={contactFormSchema}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              isValid,
              setFieldValue,
            }) => {
              return (
                <Form className="contact-us-hero__form">
                  <Grid container spacing={2}>
                    {/* First Name */}
                    <Grid item xs={12} md={6}>
                      <Typography className="contact-us-hero__label">
                        First Name
                        <span className="contact-us-hero__required">*</span>
                      </Typography>
                      <TextField
                        name="firstName"
                        placeholder="Enter first name"
                        InputProps={compactInputProps}
                        fullWidth
                        value={values.firstName}
                        sx={{
                          input: {
                            color: "#104145 !important",
                          },
                        }}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(
                            nameInputFilterRegex,
                            ""
                          );
                          handleChange({
                            target: {
                              name: "firstName",
                              value: cleaned,
                            },
                          });
                        }}
                        error={touched.firstName && Boolean(errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                      />
                    </Grid>

                    {/* Last Name */}
                    <Grid item xs={12} md={6}>
                      <Typography className="contact-us-hero__label">
                        Last Name
                        <span className="contact-us-hero__required">*</span>
                      </Typography>
                      <TextField
                        name="lastName"
                        placeholder="Enter last name"
                        InputProps={compactInputProps}
                        fullWidth
                        value={values.lastName}
                        sx={{
                          input: {
                            color: "#104145 !important",
                          },
                        }}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(
                            nameInputFilterRegex,
                            ""
                          );
                          handleChange({
                            target: {
                              name: "lastName",
                              value: cleaned,
                            },
                          });
                        }}
                        error={touched.lastName && Boolean(errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                      />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12} md={6}>
                      <Typography className="contact-us-hero__label">
                        Email
                        <span className="contact-us-hero__required">*</span>
                      </Typography>
                      <TextField
                        name="email"
                        type="email"
                        placeholder="Enter Email"
                        InputProps={compactInputProps}
                        fullWidth
                        value={values.email}
                        sx={{
                          input: {
                            color: "#104145 !important",
                          },
                        }}
                        onChange={(e) => {
                          // allow only valid email chars as you type
                          const emailRegex = /^[a-zA-Z0-9@._+-]*$/;
                          if (emailRegex.test(e.target.value)) {
                            handleChange(e);
                          }
                        }}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>

                    {/* Phone */}
                    <Grid item xs={12} md={6}>
                      <Typography className="contact-us-hero__label">
                        Phone Number
                        <span className="contact-us-hero__required">*</span>
                      </Typography>
                      <Box
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "4px !important",
                            height: "39px !important",
                            maxWidth: "100%",
                          },
                          "& .MuiInputBase-root": {
                            fontSize: "14px",
                            height: "39px !important",
                            padding: "10px 6px !important",
                          },
                          "& .MuiInputAdornment-root": {
                            backgroundColor: "#FFFFFF",
                            borderRadius: "6px 0 0 6px",
                          },
                          "& input": {
                            color: "#104145 !important",
                          },
                        }}
                      >
                        <NewPhoneNumberComponent
                          value={values.mobileNumber}
                          onChange={(mobileValue: string) =>
                            handleChange({
                              target: {
                                name: "mobileNumber",
                                value: mobileValue,
                              },
                            })
                          }
                          countryCode={countryCode}
                          onCountryChange={setCountryCode}
                          placeholder="Enter phone number"
                          error={
                            touched.mobileNumber && Boolean(errors.mobileNumber)
                          }
                        />
                      </Box>
                    </Grid>

                    {/* Select Option 1 */}
                    <Grid item xs={12} md={6}>
                      <Typography className="contact-us-hero__label">
                        Inquiry Made On Behalf Of
                        <span className="contact-us-hero__required">*</span>
                      </Typography>
                      <TextField
                        select
                        name="inquiryMadeOnBehalfOf"
                        value={values.inquiryMadeOnBehalfOf}
                        sx={{
                          input: {
                            color: "#104145 !important",
                          },
                        }}
                        onChange={handleChange}
                        InputProps={compactInputProps}
                        fullWidth
                        error={
                          touched.inquiryMadeOnBehalfOf &&
                          Boolean(errors.inquiryMadeOnBehalfOf)
                        }
                        helperText={
                          touched.inquiryMadeOnBehalfOf &&
                          errors.inquiryMadeOnBehalfOf
                        }
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected: unknown) => {
                            const value = selected as string;

                            if (value === "") {
                              return (
                                <span style={{ color: "#999999" }}>
                                  -- Select an Option --
                                </span>
                              );
                            }
                            const selectedOption = inquiryOptions.find(
                              (opt) => opt.value === value
                            );
                            return selectedOption
                              ? selectedOption.label
                              : value;
                          },
                        }}
                      >
                        {inquiryOptions.map((opt) => (
                          <MenuItem key={opt.label} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Select Option 2 */}
                    <Grid item xs={12} md={6}>
                      <Typography className="contact-us-hero__label">
                        Reason for Inquiry
                        <span className="contact-us-hero__required">*</span>
                      </Typography>
                      <TextField
                        select
                        name="reasonForInquiry"
                        fullWidth
                        InputProps={compactInputProps}
                        value={values.reasonForInquiry}
                        onChange={handleChange}
                        sx={{
                          input: {
                            color: "#104145 !important",
                          },
                        }}
                        error={
                          touched.reasonForInquiry &&
                          Boolean(errors.reasonForInquiry)
                        }
                        helperText={
                          touched.reasonForInquiry && errors.reasonForInquiry
                        }
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected: any) => {
                            if (!selected) {
                              return (
                                <span style={{ color: "#999999" }}>
                                  -- Select an Option --
                                </span>
                              );
                            }
                            const selectedOption = reasonForInquiryOptions.find(
                              (opt) => opt.value === selected
                            );
                            return selectedOption
                              ? selectedOption.label
                              : selected;
                          },
                        }}
                      >
                        {reasonForInquiryOptions.map((opt) => (
                          <MenuItem key={opt.label} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Message */}
                    <Grid item xs={12}>
                      <Typography className="contact-us-hero__label">
                        Message
                      </Typography>
                      <TextField
                        name="message"
                        fullWidth
                        placeholder="Write Something"
                        multiline
                        rows={5}
                        value={values.message}
                        onChange={handleChange}
                        sx={{
                          input: {
                            color: "#104145 !important",
                          },
                        }}
                        error={touched.message && Boolean(errors.message)}
                        helperText={touched.message && errors.message}
                      />
                    </Grid>

                    {/* Checkbox */}
                    <Grid item xs={12} md={6}>
                      <Box className="contact-us-hero__recaptcha-container">
                        <Recaptcha
                          ref={recaptchaRef}
                          siteKey={captchaKey}
                          onVerify={(token) =>
                            handleRecaptchaVerify(token, setFieldValue)
                          }
                        />
                      </Box>
                    </Grid>

                    {/* Submit Button */}
                    <Grid
                      item
                      xs={12}
                      md={6}
                      className="contact-us-hero__submit-wrapper"
                    >
                      <Button
                        type="submit"
                        disabled={
                          !isValid ||
                          !values.captchaToken ||
                          isLoading ||
                          !emailValidationRegex.test(values.email)
                        }
                        className={`contact-us-hero__submit-wrapper-btn ${
                          !(
                            !isValid ||
                            !values.captchaToken ||
                            isLoading ||
                            !emailValidationRegex.test(values.email)
                          )
                            ? "enabled-btn"
                            : "disabled-btn"
                        }`}
                      >
                        Submit
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Box>
        <Modal open={openModal} onClose={handleCloseModal}>
          <Box className="contact-us-hero__modal">
            <Box className="contact-us-hero__modal-header">
              <Typography className="contact-us-hero__modal-title">
                Submission Successful
              </Typography>
              <IconButton
                className="contact-us-hero__modal-close"
                onClick={handleCloseModal}
              >
                <Close fontSize="large" />
              </IconButton>
            </Box>

            <Typography className="contact-us-hero__modal-message">
              Thanks for contacting us! We will get in touch with you shortly.
            </Typography>

            <Box className="contact-us-hero__modal-footer">
              <Button
                onClick={handleCloseModal}
                className="contact-us-hero__modal-ok-btn"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
}
